import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import type {
  CreateLeadPayload,
  DuplicateCandidate,
  LeadDetail,
  LeadListItem,
  LeadListResponse,
  LeadTimelineEvent,
  MergeLeadsPayload,
  UpdateLeadPayload,
} from "../types";

const KEY = ["leads"] as const;

export type LeadListParams = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  ownerId?: string;
  hasEnquiries?: "yes" | "no";
  dateFrom?: string;
  dateTo?: string;
  tab?: "all" | "new" | "mine" | "customers" | "lost";
};

export function useLeadList(
  params: LeadListParams = {},
): UseQueryResult<LeadListResponse> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<LeadListResponse>("/leads", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
          ownerId: params.ownerId,
          hasEnquiries: params.hasEnquiries,
          dateFrom: params.dateFrom,
          dateTo: params.dateTo,
          tab: params.tab,
        },
      }),
  });
}

export function useLead(id: string): UseQueryResult<LeadDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ lead: LeadDetail }>(`/leads/${id}`, {
        token: accessToken,
      });
      return res.lead;
    },
  });
}

export function useCreateLead() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateLeadPayload) =>
      apiFetch<{ lead: LeadDetail }>("/leads", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateLead(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateLeadPayload) =>
      apiFetch<{ lead: LeadDetail }>(`/leads/${id}`, {
        method: "PATCH",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAssignLeadOwner(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ownerId: string | null) =>
      apiFetch<{ lead: LeadDetail }>(`/leads/${id}/assign`, {
        method: "POST",
        body: { ownerId },
        token: accessToken,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteLead() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/leads/${id}`, { method: "DELETE", token: accessToken }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useFindDuplicates(id: string, enabled = false) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...KEY, "duplicates", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id && enabled,
    queryFn: async () => {
      const res = await apiFetch<{ candidates: DuplicateCandidate[] }>(
        `/leads/${id}/find-duplicates`,
        { token: accessToken },
      );
      return res.candidates;
    },
  });
}

export function useMergeLeads(targetLeadId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: MergeLeadsPayload) =>
      apiFetch<{ lead: LeadDetail }>(`/leads/${targetLeadId}/merge`, {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ["enquiries"] });
    },
  });
}

export function useLeadTimeline(id: string) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...KEY, "timeline", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ events: LeadTimelineEvent[] }>(
        `/leads/${id}/timeline`,
        { token: accessToken },
      );
      return res.events;
    },
  });
}

// Search the Lead picker for the "Link to existing Lead" modal on Enquiry detail.
export function useLeadPicker(query: string, enabled = true) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...KEY, "pick", query, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && enabled && query.trim().length > 0,
    queryFn: async () => {
      const res = await apiFetch<{ leads: LeadListItem[] }>("/leads/pick", {
        token: accessToken,
        query: { q: query },
      });
      return res.leads;
    },
  });
}

// Convenience: manual link/unlink for the Enquiry detail page.
export function useLinkEnquiryToLead(enquiryId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) =>
      apiFetch(`/enquiries/${enquiryId}/link-lead`, {
        method: "POST",
        body: { leadId },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["enquiries"] });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUnlinkEnquiryFromLead(enquiryId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/enquiries/${enquiryId}/unlink-lead`, {
        method: "POST",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["enquiries"] });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export const useLeadStatuses = () => useCategoryItems("lead_status");
